import React, { useState, useRef } from "react";
import Select from "react-select";

const ContactForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [service, setService] = useState(null);
  const [phone, setPhone] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Create a ref for the alert container
  const alertRef = useRef(null);

  const options = [
    { label: "Sales", value: "sales" },
    { label: "Block Management", value: "Block Management" },
    { label: "Residential Management", value: "residential Management" },
    { label: "Property Management", value: "Property Management" },
    { label: "Legal", value: "Legal" },
    { label: "Commercial Management", value: "Commercial Management" },
    { label: "Compliance Management", value: "Compliance Management" },
  ];

  const ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "a636e420-0d6d-4050-a69d-5ce05c875294";

  const scrollToAlert = () => {
    // Small delay to ensure the alert is rendered
    setTimeout(() => {
      if (alertRef.current) {
        const alertPosition = alertRef.current.getBoundingClientRect().top + window.pageYOffset;
        // Scroll to alert with offset to give it some breathing room
        window.scrollTo({
          top: alertPosition - 20, // 20px padding from top
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const closeModalAndRefresh = () => {
    setSent(false);
    window.location.reload();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSent(false);

    const formData = new FormData();
    formData.append("access_key", ACCESS_KEY);
    formData.append("subject", "New Contact Form Submission");
    formData.append("from_name", name);
    formData.append("name", name);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("service", service?.value || 'Not specified');
    formData.append("message", message);
    formData.append("gdpr", "I consent to Quoin Management storing the information I submit so that it can respond to my enquiry.");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSubmittedName(name);
        setSent(true);

        // Reset form
        setName("");
        setEmail("");
        setMessage("");
        setService(null);
        setPhone("");
        setGdpr(false);

        // Refresh the page once the user has had time to read the modal
        setTimeout(closeModalAndRefresh, 6000);
      } else {
        setError(data.message || "Something went wrong. Please try again.");
        scrollToAlert();
      }
    } catch (error) {
      console.error("Error:", error);
      setError("Failed to send message. Please try again.");
      scrollToAlert();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ltn__contact-message-area mb-120 mb--100">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div className="ltn__form-box contact-form-box box-shadow white-bg">
              <h4 className="title-2">Contact Us</h4>
              
              {/* Alert Container with ref */}
              <div ref={alertRef}>
                {/* Error Alert */}
                {error && (
                  <div 
                    style={{
                      backgroundColor: '#f8d7da',
                      color: '#721c24',
                      border: '2px solid #f5c6cb',
                      borderRadius: '8px',
                      padding: '15px 20px',
                      marginBottom: '20px',
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: '500',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      animation: 'slideDown 0.3s ease-out',
                      position: 'relative',
                      zIndex: 1000
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '24px', marginRight: '10px' }}>❌</span>
                      <div>
                        <strong>Error:</strong> {error}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <form id="contact-form" onSubmit={handleSubmit}>
                {/* Hidden fields for Web3Forms */}
                <input 
                  type="hidden" 
                  name="access_key" 
                  value={ACCESS_KEY} 
                />
                <input 
                  type="hidden" 
                  name="subject" 
                  value="New Contact Form Submission" 
                />
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="input-item input-item-name ltn__custom-icon">
                      <input
                        type="text"
                        name="name"
                        placeholder="Enter your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-item input-item-email ltn__custom-icon">
                      <input
                        type="email"
                        name="email"
                        placeholder="Enter email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-item">
                      <Select
                        options={options}
                        value={service}
                        onChange={setService}
                        isMulti={false}
                        required
                        isDisabled={loading}
                        placeholder="Select a service..."
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-item input-item-phone ltn__custom-icon">
                      <input
                        type="text"
                        name="phone"
                        placeholder="Enter phone number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="input-item input-item-textarea ltn__custom-icon">
                  <textarea
                    name="message"
                    placeholder="Enter message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    disabled={loading}
                    rows="5"
                  />
                </div>
                
                {/* GDPR Agreement */}
                <div className="form-group mb-3">
                  <p className="font-weight-bold mb-2">GDPR Agreement *</p>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      name="gdpr"
                      id="gdpr"
                      className="form-check-input"
                      checked={gdpr}
                      onChange={(e) => setGdpr(e.target.checked)}
                      required
                      disabled={loading}
                    />
                    <label htmlFor="gdpr" className="form-check-label text-secondary">
                      I consent to Quoin Management storing the information I submit so that it can respond to my enquiry.
                    </label>
                  </div>
                </div>
                
                <div className="btn-wrapper mt-0">
                  <button
                    className="btn theme-btn-1 btn-effect-1 text-uppercase"
                    type="submit"
                    disabled={loading}
                    style={{
                      opacity: loading ? 0.7 : 1,
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'SENDING...' : 'SUBMIT'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {sent && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-success-title"
          onClick={closeModalAndRefresh}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            animation: 'fadeIn 0.25s ease-out'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: '10px',
              maxWidth: '480px',
              width: '100%',
              padding: '40px 30px 30px',
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
              animation: 'popUp 0.3s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <div style={{ fontSize: '56px', lineHeight: 1, marginBottom: '20px' }}>✅</div>
            <h4 id="contact-success-title" className="title-2 mb-10">
              Thank you{submittedName ? `, ${submittedName}` : ''}!
            </h4>
            <p style={{ marginBottom: '25px' }}>
              Your message has been sent successfully. We'll get back to you within 24 hours.
            </p>
            <div className="btn-wrapper mt-0">
              <button
                type="button"
                className="btn theme-btn-1 btn-effect-1 text-uppercase"
                onClick={closeModalAndRefresh}
              >
                Close
              </button>
            </div>
            {/* Progress bar until the page refreshes */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              height: '4px',
              backgroundColor: '#28a745',
              width: '100%',
              animation: 'shrink 6s linear forwards'
            }} />
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>
        {`
          @keyframes slideDown {
            from {
              transform: translateY(-20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }

          @keyframes shrink {
            from {
              width: 100%;
            }
            to {
              width: 0%;
            }
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes popUp {
            from {
              transform: scale(0.9) translateY(10px);
              opacity: 0;
            }
            to {
              transform: scale(1) translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ContactForm;


// import React, { useState } from "react";
// import Select from "react-select";
// import AWS from "aws-sdk";

// const ContactForm = () => {
//   const [name, setName] = useState("");
//   const [email, setEmail] = useState("");
//   const [message, setMessage] = useState("");
//   const [service, setService] = useState(null);
//   const [phone, setPhone] = useState("");
//   const [sent, setSent] = useState(false);
//   const [error, setError] = useState(null);

// AWS.config.update({
//   accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
//   secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
//   region: process.env.REACT_APP_AWS_REGION || "us-east-1",
// });

//   const ses = new AWS.SES({ apiVersion: "2010-12-01" });

//   console.log(service);
//   const params = {
//     Destination: {
//       ToAddresses: ["chavez.king@quoinmanagement.com"],
//     },
//     Message: {
//       Body: {
//         Text: {
//           Data: `Name: ${name}, \n Email: ${email}, \n message: ${message}, \n service: ${service?.value}, \n phone: ${phone}, \n \n Thank You`,
//         },
//       },
//       Subject: {
//         Data: `New contact from ${name}`,
//       },
//     },
//     Source: `chavez.king@quoinmanagement.com`,
//   };

//   const options = [
//     { label: "sales", value: "sales" },
//     { label: "Property Management", value: "Property Management" },
//     { label: "residential Management", value: "residential Management" },
//     { label: "Legal", value: "Legal" },
//     { label: "Commercial Management", value: "Commercial Management" },
//     { label: "Comliance Management", value: "Comliance Management" },
//   ];

//   const eMailer = (e) => {
//     e.preventDefault();

//     {console.log(
//         service?.value,
//         email,
//         message,
//         phone,
//         sent,
//         name
//       )}
      
//     ses.sendEmail(params, (err, data) => {
//       if (err) {
//         alert("OOPS! Something went wrong!!");
//         setError("OOPS! Something went wrong!!");
//         console.error("Error sending email:", err);
//       } else {
//         setSent(true);
//         console.log("Email sent successfully:", data);
//       }
//     });
//   };

//   return (
//     <div className="ltn__contact-message-area mb-120 mb--100">
//       <div className="container">
//         <div className="row">
//           <div className="col-lg-12">
//             <div className="ltn__form-box contact-form-box box-shadow white-bg">
//               <h4 className="title-2">Contact Us</h4>
//               <form
//                 id="contact-form"
//                 onSubmit={eMailer}
//                 name="contactFormEmail"
//               >
//                 <div className="row">
//                   <div className="col-md-6">
//                     <div className="input-item input-item-name ltn__custom-icon">
//                       <input
//                         type="text"
//                         name="name"
//                         placeholder="Enter your name"
//                         onChange={(e) => {
//                           setName(e.target.value);
//                         }}
//                         required
//                       />
//                     </div>
//                   </div>
//                   <div className="col-md-6">
//                     <div className="input-item input-item-email ltn__custom-icon">
//                       <input
//                         type="email"
//                         name="email"
//                         placeholder="Enter email address"
//                         onChange={(e) => {
//                           setEmail(e.target.value);
//                         }}
//                         required
//                       />
//                     </div>
//                   </div>
//                   <div className="col-md-6">
//                     <div className="input-item">
//                       <Select
//                         options={options}
//                         value={service}
//                         onChange={(values) => setService(values)}
//                         isMulti={false}
//                         required
//                       />
//                     </div>
//                   </div>
//                   <div className="col-md-6">
//                     <div className="input-item input-item-phone ltn__custom-icon">
//                       <input
//                         type="text"
//                         name="phone"
//                         placeholder="Enter phone number"
//                         onChange={(e) => {
//                           setPhone(e.target.value);
//                         }}
//                         required
//                       />
//                     </div>
//                   </div>
//                 </div>
//                 <div className="input-item input-item-textarea ltn__custom-icon">
//                   <textarea
//                     name="message"
//                     placeholder="Enter message"
//                     defaultValue={""}
//                     onChange={(e) => {
//                       setMessage(e.target.value);
//                     }}
//                     required
//                   />
//                 </div>
//                 <p>
//                   {/* <label className="input-info-save mb-0">
//                                         <input type="checkbox" name="agree" /> Save my name,
//                                         email, and website in this browser for the next time I
//                                         comment.
//                                     </label> */}
//                 </p>
//                 <div className="btn-wrapper mt-0">
//                   <button
//                     className="btn theme-btn-1 btn-effect-1 text-uppercase"
//                     type="submit"
//                   >
//                     submit
//                   </button>
//                 </div>
//                 <p className="mb-0 mt-20" style={{ color: "green" }}>
//                   {sent ? "Message Sent Successfully" : null}
//                 </p>
//                 <p className="mb-0 mt-20" style={{ color: "red" }}>
//                   {error ? error : null}
//                 </p>
//               </form>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ContactForm;
